import { api } from '@/lib/axios';

export type MeetingStatus = 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
export type MeetingType = 'INSTANT' | 'SCHEDULED' | 'RECURRING' | 'PERSONAL';

export interface Meeting {
  id: string;
  meetingCode: string;
  title: string;
  description: string | null;
  hostId: string;
  type: MeetingType;
  status: MeetingStatus;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  timezone: string;
  recurrence: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  waitingRoom: boolean;
  muteOnEntry: boolean;
  allowGuests: boolean;
  hasPassword: boolean;
  maxParticipants: number;
  inviteUrl: string;
  joinUrl: string;
  createdAt: string;
}

export interface Participant {
  id: string;
  meetingId: string;
  userId: string | null;
  guestName: string | null;
  role: 'HOST' | 'CO_HOST' | 'PARTICIPANT';
  status: 'INVITED' | 'WAITING' | 'ADMITTED' | 'JOINED' | 'LEFT' | 'REMOVED';
  audioEnabled: boolean;
  videoEnabled: boolean;
  isPinned: boolean;
  isSpotlighted: boolean;
  joinedAt: string | null;
}

export interface CreateScheduledPayload {
  title: string;
  description?: string;
  scheduledStart: string;
  scheduledEnd: string;
  timezone?: string;
  password?: string;
  waitingRoom?: boolean;
  muteOnEntry?: boolean;
  allowGuests?: boolean;
  maxParticipants?: number;
  recurrence?: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
}

export interface JoinResult {
  meetingId: string;
  participant: Participant;
  waiting: boolean;
}

export const meetingApi = {
  async listMine(params: { page?: number; limit?: number; status?: MeetingStatus; search?: string } = {}) {
    const { data } = await api.get('/meetings', { params });
    return data as { data: { meetings: Meeting[] }; meta: { page: number; total: number; totalPages: number } };
  },
  async createInstant(title = 'Instant Meeting'): Promise<Meeting> {
    const { data } = await api.post('/meetings/instant', { title });
    return data.data.meeting;
  },
  async createScheduled(payload: CreateScheduledPayload): Promise<Meeting> {
    const { data } = await api.post('/meetings', payload);
    return data.data.meeting;
  },
  async personalRoom(): Promise<Meeting> {
    const { data } = await api.get('/meetings/personal-room');
    return data.data.meeting;
  },
  async get(id: string): Promise<Meeting> {
    const { data } = await api.get(`/meetings/${id}`);
    return data.data.meeting;
  },
  async joinInfo(code: string) {
    const { data } = await api.get(`/meetings/info/${encodeURIComponent(code)}`);
    return data.data as {
      id: string;
      title: string;
      hasPassword: boolean;
      waitingRoom: boolean;
      status: MeetingStatus;
      allowGuests: boolean;
    };
  },
  async join(code: string, opts: { password?: string; guestName?: string } = {}): Promise<JoinResult> {
    const { data } = await api.post('/meetings/join', { code, ...opts });
    return data.data;
  },
  async cancel(id: string): Promise<void> {
    await api.delete(`/meetings/${id}`);
  },
  async start(id: string): Promise<Meeting> {
    const { data } = await api.post(`/meetings/${id}/start`);
    return data.data.meeting;
  },
  async end(id: string): Promise<Meeting> {
    const { data } = await api.post(`/meetings/${id}/end`);
    return data.data.meeting;
  },
};
